// Design tokens consumed by ECharts (which can't read CSS variables in
// itself). Keep these aligned with the corresponding `--c1`-`--c6`,
// `--pos`, `--neg`, `--card-ink`, `--card-muted`, `--rule` declarations
// in styles/tokens.css. The CSS variants are the source of truth for
// HTML/SFC styles; this module is the runtime mirror for canvas charts.

export const PALETTE = [
  "#6aceb0", // --c1 — primary teal
  "#5b9cf2", // --c2 — blue
  "#f4a261", // --c3 — orange
  "#e07b91", // --c4 — pink
  "#b18cf0", // --c5 — purple
  "#f3c94a", // --c6 — yellow
] as const;

/**
 * Mono font stack for chart text (axis ticks, legend, tooltip).
 *
 * The rest of the embed surface — KPI labels, table headers, eyebrow
 * titles — commits to a mono/uppercase editorial register via the
 * `--mono` CSS variable. Canvas-rendered ECharts text can't resolve
 * `var(--mono)`, so this is the runtime mirror, same pattern as the
 * `COLORS` / `CHART_COLORS` tables above.
 *
 * Picked to match the `var(--mono, ui-monospace, monospace)` fallback
 * chain used in `EmbedHost.vue` and `InsightTable.vue`, so charts read
 * as part of the same family even when the embedder hasn't supplied a
 * custom `--mono` value.
 */
export const FONT_STACK_MONO =
  "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace";

/**
 * Default line width for time-series and continuous chart strokes
 * (line, area, mixed-line). Slightly heavier than ECharts' 2px default
 * so series read confidently against the canvas without crossing into
 * "marketing infographic" territory.
 */
export const LINE_STROKE_WIDTH = 2.5;

function hexToRgba(hex: string, alpha: number): string {
  const h = hex.replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

/**
 * Vertical linear-gradient `areaStyle.color` for the palette colour at
 * `index`. Saturated at the top of the area, fading to near-transparent
 * at the baseline — the editorial pop that flat fills can't deliver
 * and the reason we pin the canvas renderer (gradients work the same
 * either way, but this is canvas-safe by construction).
 */
export function areaGradient(index: number): object {
  const base = PALETTE[index % PALETTE.length];
  return {
    type: "linear",
    x: 0,
    y: 0,
    x2: 0,
    y2: 1,
    colorStops: [
      { offset: 0, color: hexToRgba(base, 0.55) },
      { offset: 1, color: hexToRgba(base, 0.05) },
    ],
  };
}

export const COLORS = {
  primary: "#3aa988", // --primary-deep
  positive: "#3aa988", // --pos
  negative: "#d96363", // --neg
  // Status colours for callout `kind: "info"` / `kind: "warn"`. `success`
  // and `danger` map to `positive` / `negative` above. Surfaced via
  // `--lucent-status-info` / `--lucent-status-warn` on the host element;
  // callout components consume these as fallbacks in their CSS tokens.
  statusInfo: "#5b9cf2", // --status-info
  statusWarn: "#f3c94a", // --status-warn
  background: "#f7f6f2", // --paper
  text: "#0e0e10", // --card-ink
  textMuted: "#76767e", // --card-muted
  grid: "#e5e3dc", // --rule
  // Chart-text and chart-line tones. The global `text` / `textMuted` /
  // `grid` tokens above are cool neutrals (blue-ish), but the rest of
  // the embed surface — paper, rule, cards — is warm-tinted. When
  // chart text and gridlines used the cool greys they read as "bolted
  // on" against the warm card. These chart-specific tokens are nudged
  // toward the paper's warmth so axes, ticks, and the legend belong to
  // the same family as the chrome around them.
  //
  // The split between `chartAxisLine` and `chartGridLine` gives the
  // X-axis baseline more presence than the dashed Y-axis guides —
  // the chart "stands on" the baseline, and the guides recede.
  chartLabelStrong: "#3d3a34", // legend — warm charcoal, less harsh than `text` at full bold uppercase
  chartLabelMuted: "#8c867a",  // axis ticks — warm mid-grey, replaces cool `textMuted` on canvas
  chartAxisLine: "#bfb8a8",    // X-axis baseline — defined warm rule, sits below ticks
  chartGridLine: "#dcd5c4",    // Y-axis split gridlines — dashed, recede behind data
} as const;

/**
 * Per-chart canvas-safe colour tokens. ECharts paints to canvas
 * (renderer pinned in `echarts.ts` for round-4 security M-5); canvas's
 * `fillStyle` does NOT resolve CSS `var(--…)` references, so chart
 * options must receive concrete hex / rgb strings. The CSS tokens
 * referenced in `briefing.md` are the embedder-facing knobs; this
 * module is what ECharts actually consumes.
 *
 * Keep aligned with the corresponding `--lucent-*` declarations.
 * Embedder restyle that needs to reach a canvas chart has to go
 * through the appropriate spec field (e.g. `divergent.positiveColor`,
 * `gauge.thresholds[].color`) which the host resolves before passing
 * to ECharts — not via CSS variables on the host element.
 */
export const CHART_COLORS = {
  // gauge — green / amber / red ramp.
  gaugeThresholdLow: "#d96363", // --neg
  gaugeThresholdMid: "#f3c94a", // --c6
  gaugeThresholdHigh: "#3aa988", // --pos
  gaugeNeedle: "#0e0e10", // --card-ink
  gaugeTrack: "#e5e3dc", // --rule
  // waterfall + divergent defaults (positive / negative carry over
  // from COLORS but per-chart tokens stay namespaced so embedder
  // restyle can target one without touching the other).
  waterfallPositive: "#3aa988",
  waterfallNegative: "#d96363",
  waterfallTotal: "#0e0e10",
  // candlestick — up/down semantics match positive/negative.
  candlestickUp: "#3aa988",
  candlestickDown: "#d96363",
  // sankey
  sankeyNodeFill: "#3aa988",
  // sunburst
  sunburstStroke: "#ffffff",
  sunburstLabel: "#0e0e10",
  // tree (orthogonal node-link). `treeNode` is the single-accent
  // node fill when `colorBy` is omitted; `treeEdge` is the line
  // colour between parent and child. Embedder restyle via the
  // `--lucent-tree-node` / `--lucent-tree-edge` CSS variables.
  treeNode: "#3aa988",
  treeEdge: "#cdc9be",
  // funnel
  funnelStageStroke: "#ffffff",
  funnelStageLabel: "#ffffff",
  // calendar
  calendarEmpty: "#f3f1ea",
  chartEmptyBg: "#f7f6f2",
  chartEmptyFg: "#76767e",
} as const;
